<template>
  <div class="structure-tree">
    <div
      class="structure-tree__canvas"
      :style="{ width: layout.width + 'px', height: layout.height + 'px' }">

      <div
        class="structure-tree__line"
        :key="line.key"
        :style="lineStyle(line)"
        v-for="line in layout.lines">
      </div>

      <div
        class="structure-tree__frame"
        :key="frame.key"
        :style="lineStyle(frame)"
        v-for="frame in layout.frames">
      </div>

      <ParagraphStructureTreeBox
        :key="box.key"
        :name="box.name"
        :group="box.group"
        :left="box.left"
        :top="box.top"
        :width="box.width"
        :height="box.height"
        :background-color="box.backgroundColor"
        :is-root="box.isRoot"
        v-for="box in layout.boxes"
      />

    </div>
  </div>
</template>

<script>
import ParagraphStructureTreeBox from '@blocks/paragraphs/ParagraphStructureTreeBox'

export default {
  name: 'ParagraphStructureTree',
  props: {
    title: {
      type: String,
      required: true
    },
    group: {
      type: String,
      required: false,
      default: null
    },
    rootColor: {
      type: String,
      required: false,
      default: null
    },
    // Draws a short stub line above the root box, touching the very top
    // edge of the canvas - used to chain several trees together visually
    // without leaving a gap, without guessing another element's position.
    connectAbove: {
      type: Boolean,
      required: false,
      default: false
    },
    // Extends the main trunk line down to the canvas's own bottom edge
    // (instead of stopping at the last branch) so the next tree's
    // connectAbove stub has something to touch. Only needed when another
    // tree follows this one - the last tree in a list doesn't need it.
    connectBelow: {
      type: Boolean,
      required: false,
      default: false
    },
    // tree: Array<TreeNode>, TreeNode = {
    //   id, name, group, color, slot: 'level1'|'level2'|'level3',
    //   children:
    //     - when slot is 'level1'/'level2': Array<TreeNode> (deeper tiered nodes)
    //     - when slot is 'level3': Array<{id, name, group, color}> ("level 4" leaves, fanned out)
    // }
    tree: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  components: {
    ParagraphStructureTreeBox
  },
  data () {
    return {
      rootWidth: 240,
      rootHeight: 65,
      boxWidth: 168,
      boxHeight: 50,
      rootGap: 2,
      level4Gap: 12,
      level4RowGap: 10,
      framePadding: 12,
      frameGap: 15,
      firstRowY: 100,
      rowHeight: 90,
      topConnectorHeight: 30,
      canvasBottomMargin: 20,
      defaultColor: '#d9d9d9'
    }
  },
  computed: {
    colX () {
      const level1 = this.rootWidth + this.rootGap

      // The trunk drops from the root box's horizontal center, so its own
      // connecting line is at least half the root's width long (otherwise
      // level 1 would overlap the root box). level1-2 and level2-3 use that
      // same length, so every connecting line ends up equally long.
      const lineLength = this.rootWidth / 2 + this.rootGap

      return {
        level1,
        level2: level1 + this.boxWidth + lineLength,
        level3: level1 + (this.boxWidth + lineLength) * 2
      }
    },
    rootTrunkX () {
      return this.rootWidth / 2
    },
    layout () {
      const boxes = []
      const lines = []
      const frames = []
      const topOffset = this.connectAbove ? this.topConnectorHeight : 0
      let cursorY = this.firstRowY + topOffset
      let maxY = this.rootHeight + topOffset
      let hasLevel4Fan = false
      let lineKey = 0

      if (this.connectAbove) {
        lines.push({ key: 'connect-above', left: this.rootTrunkX - 1, top: 0, width: 2, height: topOffset })
      }

      boxes.push({
        key: 'root',
        left: 0,
        top: topOffset,
        width: this.rootWidth,
        height: this.rootHeight,
        backgroundColor: this.rootColor || '#3fae49',
        name: this.title,
        group: this.group,
        isRoot: true
      })

      const nextRowY = (extraHeight) => {
        const y = cursorY
        cursorY += this.rowHeight + extraHeight
        return y
      }

      const addBox = (x, y, color, name, group, key, width, height) => {
        const w = width || this.boxWidth
        const h = height || this.boxHeight

        boxes.push({
          key,
          left: x,
          top: y - h / 2,
          width: w,
          height: h,
          backgroundColor: color || this.defaultColor,
          name,
          group,
          isRoot: false
        })
        maxY = Math.max(maxY, y + h / 2)
      }

      const addHLine = (xa, xb, y) => {
        lines.push({ key: `l${lineKey++}`, left: Math.min(xa, xb), top: y - 1, width: Math.abs(xb - xa), height: 2 })
      }

      const addVLine = (x, y1, y2) => {
        lines.push({ key: `l${lineKey++}`, left: x - 1, top: y1, width: 2, height: y2 - y1 })
      }

      // Level 4 items are grouped 2-per-row inside a single frame, and only
      // the frame itself is connected to its level 3 parent (one line) -
      // no lines between the items or between the frame and each of them.
      const frameSize = (count) => {
        const cols = Math.min(count, 2)
        const rows = Math.ceil(count / 2)

        return {
          rows,
          width: cols * this.boxWidth + (cols - 1) * this.level4Gap + this.framePadding * 2,
          height: rows * this.boxHeight + (rows - 1) * this.level4RowGap + this.framePadding * 2
        }
      }

      const level4ExtraHeight = (count) => {
        if (!count) {
          return 0
        }

        // A single item just hangs directly below level 3 (no frame needed).
        return count === 1 ? this.frameGap + this.boxHeight : this.frameGap + frameSize(count).height
      }

      const layoutLevel4 = (node, x, centerYParent) => {
        const children = node.children
        const parentCenterX = x + this.boxWidth / 2
        const parentBottom = centerYParent + this.boxHeight / 2

        if (children.length === 1) {
          const child = children[0]
          const boxLeft = parentCenterX - this.boxWidth / 2
          const cy = parentBottom + this.frameGap + this.boxHeight / 2
          addVLine(parentCenterX, parentBottom, cy - this.boxHeight / 2)
          addBox(boxLeft, cy, child.color, child.name, child.group, `b4-${child.id}`, this.boxWidth, this.boxHeight)
          return
        }

        hasLevel4Fan = true

        const size = frameSize(children.length)
        const frameLeft = parentCenterX - size.width / 2
        const frameTop = parentBottom + this.frameGap

        frames.push({
          key: `frame-${node.id}`,
          left: frameLeft,
          top: frameTop,
          width: size.width,
          height: size.height
        })

        addVLine(parentCenterX, parentBottom, frameTop)
        maxY = Math.max(maxY, frameTop + size.height)

        children.forEach((child, i) => {
          const row = Math.floor(i / 2)
          const col = i % 2
          const cx = frameLeft + this.framePadding + col * (this.boxWidth + this.level4Gap)
          const cy = frameTop + this.framePadding + row * (this.boxHeight + this.level4RowGap) + this.boxHeight / 2
          addBox(cx, cy, child.color, child.name, child.group, `b4-${child.id}`, this.boxWidth, this.boxHeight)
        })
      }

      // A node's own column is driven purely by its tier ("slot"), not by
      // how many parent_id hops separate it from its caller - an untagged
      // ancestor may have been skipped on the way here.
      const layoutNode = (node) => {
        const x = this.colX[node.slot]
        const isLeaf = node.slot === 'level3' || !node.children || !node.children.length
        let centerY

        if (isLeaf) {
          const level4Count = node.slot === 'level3' && node.children ? node.children.length : 0
          centerY = nextRowY(level4ExtraHeight(level4Count))
          addBox(x, centerY, node.color, node.name, node.group, `b-${node.id}`)

          if (level4Count > 0) {
            layoutLevel4(node, x, centerY)
          }
        } else {
          const childCenters = node.children.map(child => layoutNode(child))
          centerY = (Math.min(...childCenters) + Math.max(...childCenters)) / 2
          addBox(x, centerY, node.color, node.name, node.group, `b-${node.id}`)

          const trunkX = x + this.boxWidth
          addVLine(trunkX, Math.min(...childCenters), Math.max(...childCenters))

          node.children.forEach((child, i) => {
            addHLine(trunkX, this.colX[child.slot], childCenters[i])
          })
        }

        return centerY
      }

      const topCenters = this.tree.map(node => layoutNode(node))

      if (topCenters.length) {
        // Only runs all the way down to the canvas's own bottom edge when
        // another tree is chained below (connectBelow) - otherwise it just
        // stops at the last branch's row, like before.
        const trunkBottom = this.connectBelow ? maxY + this.canvasBottomMargin : Math.max(...topCenters)
        addVLine(this.rootTrunkX, topOffset + this.rootHeight, trunkBottom)

        this.tree.forEach((node, i) => {
          addHLine(this.rootTrunkX, this.colX[node.slot], topCenters[i])
        })
      }

      // The level 4 frame is centered under its level 3 parent and is
      // always at most 2 columns wide (see frameSize), so its rightmost
      // edge can reach past the level 3 column's own right edge.
      const level4Width = hasLevel4Fan
        ? this.colX.level3 + this.boxWidth / 2 + frameSize(2).width / 2
        : 0

      return {
        boxes,
        lines,
        frames,
        width: Math.max(this.colX.level3 + this.boxWidth + 20, level4Width + 20),
        height: maxY + this.canvasBottomMargin
      }
    }
  },
  methods: {
    lineStyle (line) {
      return {
        left: line.left + 'px',
        top: line.top + 'px',
        width: line.width + 'px',
        height: line.height + 'px'
      }
    }
  }
}
</script>

<style lang="scss">
.structure-tree {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;

  // No padding-bottom here: the canvas already reserves its own bottom
  // margin (see `height: maxY + 20` in the layout computed). An extra
  // padding here would add dead space between stacked trees, breaking the
  // "connectAbove" stub line's visual continuity with the tree above it.
  &__canvas {
    position: relative;
  }

  &__line {
    position: absolute;
    background-color: black;
  }

  &__frame {
    position: absolute;
    border: 2px solid black;
    border-radius: layout(border-radius);
    background-color: tint(black, 96%);
    box-sizing: border-box;
  }
}
</style>
